/**
 * Catchball Path spine helpers — ordered org-unit stops from Cycle Designer.
 *
 * Product rule: the Path anchor (`nodes[0]`) is the executive level that starts
 * with the ball. Launch / first pass moves the ball to the next stop (`nodes[1]`).
 * The Cycle Designer itself is configuration, not a possession tier above the
 * org chart.
 */

import {
  DESIGNER_PATH_ORG_UNITS_KEY,
  DESIGNER_PATH_PARENTS_KEY,
} from '../features/cycle-designer/lib/launchCycleDesigner';

/** Ordered Path org-unit ids from track metadata (anchor → endpoint). */
export function readDesignerPathSpine(
  metadata: Record<string, unknown> | null | undefined,
): string[] {
  const raw = metadata?.[DESIGNER_PATH_ORG_UNITS_KEY];
  if (!Array.isArray(raw)) return [];
  return raw.filter((x): x is string => typeof x === 'string' && x.length > 0);
}

/**
 * Which stop each Path stop hangs off (stop → the stop above it). Present on
 * tracks launched since paths could fork; absent on older tracks, where the
 * ordered list is a plain chain and each stop hangs off the one before it.
 */
export type PathParents = Readonly<Record<string, string>>;

export function readDesignerPathParents(
  metadata: Record<string, unknown> | null | undefined,
): PathParents | undefined {
  const raw = metadata?.[DESIGNER_PATH_PARENTS_KEY];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'string' && v.length > 0) out[k] = v;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function parentOnSpine(spine: readonly string[], stop: string, parents?: PathParents): string | undefined {
  const declared = parents?.[stop];
  if (declared && spine.includes(declared)) return declared;
  const idx = spine.indexOf(stop);
  return idx > 0 ? spine[idx - 1] : undefined;
}

export function pathAnchorOrgUnitId(spine: readonly string[]): string | undefined {
  return spine[0];
}

/**
 * Path stops strictly below the current holder (next handoff targets). On a
 * path that forks this is the holder's own branches only — never a stop on a
 * sibling branch.
 */
export function spineStopsBelowHolder(
  spine: readonly string[],
  holderOrgUnitId: string | null | undefined,
  parents?: PathParents,
): string[] {
  if (!holderOrgUnitId || spine.length === 0) return [];
  if (!spine.includes(holderOrgUnitId)) return [];
  const below = new Set<string>([holderOrgUnitId]);
  const out: string[] = [];
  // Parents come before children in the list, so one pass finds every descendant.
  for (const stop of spine) {
    const parent = parentOnSpine(spine, stop, parents);
    if (parent && below.has(parent) && stop !== holderOrgUnitId) {
      below.add(stop);
      out.push(stop);
    }
  }
  return out;
}

/** Path stops strictly above the current holder (nearest first). */
export function spineStopsAboveHolder(
  spine: readonly string[],
  holderOrgUnitId: string | null | undefined,
  parents?: PathParents,
): string[] {
  if (!holderOrgUnitId || spine.length === 0) return [];
  if (!spine.includes(holderOrgUnitId)) return [];
  const out: string[] = [];
  let cursor = parentOnSpine(spine, holderOrgUnitId, parents);
  while (cursor && out.length <= spine.length) {
    out.push(cursor);
    cursor = parentOnSpine(spine, cursor, parents);
  }
  return out;
}

/**
 * Org unit the current actor may act as for level-turn hold/pass.
 *
 * - If the ball is at a stop the user is placed on, act as that holder
 *   (Lew on Mosso + Product Leads must act as Leads when the ball is there).
 * - Else prefer the deepest Path stop among the user's placements.
 * - Cycle owner/editor may act as the Path anchor for kickoff — before a turn
 *   exists, or while the ball is still at executives — but not as a phantom
 *   tier above the Path.
 */
export function resolveActingOrgUnitId(opts: {
  userOrgUnitId?: string | null;
  /** All org units the user is assigned to (multi-placement). */
  userOrgUnitIds?: readonly string[] | null;
  pathSpine: readonly string[];
  activeHolderOrgUnitId?: string | null;
  isCycleOwnerOrEditor: boolean;
}): string | undefined {
  const {
    userOrgUnitId,
    userOrgUnitIds,
    pathSpine,
    activeHolderOrgUnitId,
    isCycleOwnerOrEditor,
  } = opts;

  const placements = new Set<string>();
  if (userOrgUnitId) placements.add(userOrgUnitId);
  for (const id of userOrgUnitIds ?? []) {
    if (id) placements.add(id);
  }

  // Ball is at a stop this user is on → they are the holder (multi-placement).
  if (activeHolderOrgUnitId && placements.has(activeHolderOrgUnitId)) {
    return activeHolderOrgUnitId;
  }

  // Prefer the deepest Path stop among placements (leaf-ward over anchor).
  if (pathSpine.length > 0) {
    for (let i = pathSpine.length - 1; i >= 0; i--) {
      const stop = pathSpine[i];
      if (placements.has(stop)) return stop;
    }
  } else if (userOrgUnitId) {
    return userOrgUnitId;
  }

  // Cycle owner/editor kickoff: act as Path anchor before the ball leaves executives.
  if (isCycleOwnerOrEditor && pathSpine.length > 0) {
    const anchor = pathSpine[0];
    if (!activeHolderOrgUnitId || activeHolderOrgUnitId === anchor) {
      return anchor;
    }
  }

  return userOrgUnitId ?? undefined;
}
